import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import FloatingPanel from "../overlays/FloatingPanel";
import "./TinyContextMenu.scss";
const TinyContextMenu = forwardRef(function TinyContextMenu(
  {
    items = [],
    minWidth = 140,
    maxWidth = 240,
    gap = 8,
    pad = 8,
    maxHeight = 320,
    customBtn = null,
    buttonColor = null,
    itemHeight = 56,
    label = "Open menu",
    menuClassName = "",
  },
  ref,
) {
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [point, setPoint] = useState(null);
  const close = useCallback(() => {
    setOpen(false);
    setPoint(null);
  }, []);
  const show = useCallback(
    (nextPoint = null) => {
      if (!items.length) return;
      document.dispatchEvent(new Event("antlogy-close-menus"));
      setPoint(nextPoint);
      setOpen(true);
    },
    [items.length],
  );
  useImperativeHandle(
    ref,
    () => ({ open: () => show(), openAt: (x, y) => show({ x, y }), close }),
    [show, close],
  );
  useEffect(() => {
    document.addEventListener("antlogy-close-menus", close);
    return () => document.removeEventListener("antlogy-close-menus", close);
  }, [close]);
  useEffect(() => {
    if (!items.length) close();
  }, [items.length, close]);
  const keys = (event) => {
    const buttons = [
      ...event.currentTarget.querySelectorAll(
        '[role="menuitem"]:not(:disabled)',
      ),
    ];
    const index = buttons.indexOf(document.activeElement);
    let next;
    if (event.key === "ArrowDown") next = (index + 1) % buttons.length;
    if (event.key === "ArrowUp")
      next = (index - 1 + buttons.length) % buttons.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = buttons.length - 1;
    if (next !== undefined && buttons.length) {
      event.preventDefault();
      buttons[next].focus();
    }
    if (event.key === "Tab") {
      event.preventDefault();
      close();
      buttonRef.current?.focus();
    }
  };
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="tiny-context-menu__trigger"
        disabled={!items.length}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        style={{ color: buttonColor ?? undefined }}
        onClick={(event) => {
          event.stopPropagation();
          if (open) close();
          else show();
        }}
        onKeyDown={(event) => {
          if (["ArrowDown", "ArrowUp"].includes(event.key)) {
            event.preventDefault();
            show();
          }
        }}
      >
        {customBtn ?? <BsThreeDotsVertical size={18} aria-hidden="true" />}
      </button>
      {open && (
        <FloatingPanel
          anchorRef={buttonRef}
          point={point}
          width={maxWidth}
          minWidth={minWidth}
          maxHeight={maxHeight}
          gap={gap}
          pad={pad}
          align="end"
          label={label}
          onClose={close}
          onKeyDown={keys}
          initialFocus={'[role="menuitem"]:not(:disabled)'}
          className={`tiny-context-menu__menu ${menuClassName}`}
        >
          {items.map((item, index) => (
            <button
              key={item.key ?? index}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              className={`tiny-context-menu__item${item.danger ? " is-danger" : ""}`}
              style={{ minHeight: itemHeight }}
              onClick={() => {
                if (item.disabled) return;
                close();
                buttonRef.current?.focus();
                item.onPress?.();
              }}
            >
              {item.icon && (
                <span
                  className="tiny-context-menu__item-icon"
                  aria-hidden="true"
                >
                  {item.icon}
                </span>
              )}
              <span className="tiny-context-menu__item-label">
                {item.label}
              </span>
            </button>
          ))}
        </FloatingPanel>
      )}
    </>
  );
});
export default TinyContextMenu;
